
import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { SetData } from "../../../types";
import { usePersistedSetData } from "./usePersistedSetData";

interface UseOptimizedSetDataProps {
  exerciseId: string;
  sets: number;
  setData: SetData[];
  reps: number;
  weight: number;
  onSetDataChange: (setIndex: number, field: 'reps' | 'weight', value: string) => void;
}

export const useOptimizedSetData = ({
  exerciseId,
  sets,
  setData,
  reps,
  weight,
  onSetDataChange
}: UseOptimizedSetDataProps) => {
  const [syncingStates, setSyncingStates] = useState<Record<number, boolean>>({});
  const [lastSyncTimes, setLastSyncTimes] = useState<Record<number, number>>({});
  
  // Hook para persistencia de datos
  const {
    getSetData: getPersistedSetData,
    saveSetData: savePersistedSetData,
    getAllSetData
  } = usePersistedSetData({
    exerciseId,
    sets,
    defaultReps: reps,
    defaultWeight: weight
  });
  
  // Referencias para backup local ultra-rápido y estabilidad anti-flicker
  const localBackupRef = useRef<Record<number, SetData>>({});
  const syncTimeoutsRef = useRef<Record<number, NodeJS.Timeout>>({});
  const lastExternalUpdateRef = useRef<number>(0);
  const stableDataRef = useRef<SetData[]>([]);

  // Inicializar setData completo con datos persistidos y backup local
  const completeSetData = useMemo(() => {
    console.log(`⚡ Ultra-fast initialization with persistence for exercise ${exerciseId}:`, {
      totalSets: sets,
      providedSetData: setData,
      defaultReps: reps,
      defaultWeight: weight
    });

    // Verificar si los datos han cambiado realmente
    const timeSinceLastExternal = Date.now() - lastExternalUpdateRef.current;
    const shouldUseStableData = timeSinceLastExternal < 1000 && stableDataRef.current.length === sets;

    if (shouldUseStableData) {
      console.log(`🔒 Using stable data to prevent flicker`);
      return stableDataRef.current;
    }

    const complete = Array.from({ length: sets }, (_, index) => {
      // Prioridad: 1) Datos persistidos confirmados por usuario, 2) Backup local, 3) Datos existentes, 4) Por defecto
      const persistedData = getPersistedSetData(index);
      const localBackup = localBackupRef.current[index];
      const existingData = setData[index];
      const defaultData = { reps, weight };
      
      let finalData: SetData;
      
      if (persistedData.isSaved) {
        // Datos confirmados por usuario tienen máxima prioridad
        finalData = { reps: persistedData.reps, weight: persistedData.weight };
        console.log(`💾 Using persisted user data for set ${index}:`, finalData);
      } else if (localBackup && (localBackup.reps !== undefined && localBackup.weight !== undefined)) {
        finalData = localBackup;
        console.log(`🔄 Using local backup for set ${index}:`, finalData);
      } else if (existingData && (existingData.reps !== undefined && existingData.weight !== undefined)) {
        finalData = existingData;
        // Actualizar backup local
        localBackupRef.current[index] = finalData;
      } else {
        finalData = defaultData;
        // Crear backup inicial
        localBackupRef.current[index] = finalData;
      }
      
      return finalData;
    });

    // Actualizar datos estables
    stableDataRef.current = complete;
    lastExternalUpdateRef.current = Date.now();

    console.log(`✅ Ultra-fast setData initialized with persistence protection:`, complete);
    return complete;
  }, [sets, setData, reps, weight, exerciseId, getPersistedSetData]);

  // Función ultra-optimizada para obtener datos de serie con persistencia
  const getCurrentSetData = useCallback((setIndex: number): SetData => {
    // Priorizar datos persistidos confirmados por usuario
    const persistedData = getPersistedSetData(setIndex);
    if (persistedData.isSaved) {
      console.log(`⚡ Ultra-fast persisted data retrieval for set ${setIndex}:`, persistedData);
      return { reps: persistedData.reps, weight: persistedData.weight };
    }
    
    // Fallback a backup local o datos globales
    const localData = localBackupRef.current[setIndex];
    const globalData = completeSetData[setIndex];
    
    const finalData = localData || globalData;
    console.log(`⚡ Ultra-fast stable data retrieval for set ${setIndex}:`, {
      persisted: persistedData,
      local: localData,
      global: globalData,
      final: finalData
    });
    
    return finalData;
  }, [completeSetData, getPersistedSetData]);

  // Handler ultra-mejorado con persistencia automática
  const handleSetDataChange = useCallback((setIndex: number, field: 'reps' | 'weight', value: string) => {
    const startTime = Date.now();
    console.log(`⚡ Ultra-fast set data change with persistence: set ${setIndex}, field ${field}, value ${value}`);
    
    // 1. Backup local inmediato con timestamp (0ms)
    const currentData = getCurrentSetData(setIndex);
    const numericValue = field === 'weight' ? parseFloat(value) || 0 : parseInt(value) || 0;
    
    const updatedData = {
      ...currentData,
      [field]: numericValue
    };
    
    localBackupRef.current[setIndex] = updatedData;
    
    // 2. Guardar en persistencia local inmediatamente
    savePersistedSetData(setIndex, updatedData.reps, updatedData.weight);
    
    // 3. Actualizar datos estables inmediatamente para evitar flicker
    const newStableData = [...stableDataRef.current];
    newStableData[setIndex] = updatedData;
    stableDataRef.current = newStableData;
    
    // 4. Indicador visual inmediato
    setSyncingStates(prev => ({ ...prev, [setIndex]: true }));
    
    // 5. Cancelar sincronización anterior si existe
    if (syncTimeoutsRef.current[setIndex]) {
      clearTimeout(syncTimeoutsRef.current[setIndex]);
    }
    
    // 6. Llamar directamente al handler padre (ultra-rápido)
    onSetDataChange(setIndex, field, value);
    
    // 7. Programar confirmación de sincronización con protección anti-flicker
    syncTimeoutsRef.current[setIndex] = setTimeout(() => {
      const syncTime = Date.now() - startTime;
      console.log(`✅ Ultra-fast sync with persistence completed for set ${setIndex} in ${syncTime}ms`);
      
      setSyncingStates(prev => ({ ...prev, [setIndex]: false }));
      setLastSyncTimes(prev => ({ ...prev, [setIndex]: syncTime }));
      
      // Limpiar timeout
      delete syncTimeoutsRef.current[setIndex];
    }, 100);
    
  }, [onSetDataChange, getCurrentSetData, savePersistedSetData]);

  // Generar botones optimizados con indicadores de estado mejorados
  const setButtons = useMemo(() => {
    const buttons = completeSetData.map((setData, index) => {
      const isCurrentlySyncing = syncingStates[index];
      const lastSyncTime = lastSyncTimes[index];
      
      return {
        index,
        reps: setData.reps,
        weight: setData.weight,
        isSyncing: isCurrentlySyncing,
        lastSyncTime: lastSyncTime
      };
    });
    
    console.log(`⚡ Generated ${buttons.length} ultra-optimized persistent set buttons:`, buttons);
    return buttons;
  }, [completeSetData, syncingStates, lastSyncTimes]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      Object.values(syncTimeoutsRef.current).forEach(clearTimeout);
    };
  }, []);

  return {
    completeSetData,
    setButtons,
    getCurrentSetData,
    handleSetDataChange,
    syncingStates,
    lastSyncTimes
  };
};
